import React, {useEffect} from 'react';
import TableHeader from 'components/table/TableHeader';
import TableRow from 'components/table/TableRow';
import TableCell from 'components/table/TableCell';
import Table from 'components/table/Table';
import {useDispatch, useSelector} from 'react-redux';
import {AppDispatch, RootState} from '../../../store/store';
import {useDropdownActionMenu} from '../../../hooks/useDropdownActionMenu';
import {SearchType} from '../../../types/search';
import {setIsEditing, setActiveTab} from '../clientSlice';
import {VisitDocumentThunks} from '../../visit/documents/visitDocumentThunks';
import {VisitDocument} from '../../../types/visitDocument';
import StatusBadge from '../../../components/StatusBadge';
import {useNavigate} from 'react-router-dom';
import DotsMenu from '../../../components/DotsMenu';

interface VisibleColumnsType {
  id: boolean;
  visitId: boolean;
  documentTemplate: boolean;
  status: boolean;
  createdAt: boolean;
  actions: boolean;
}

const ClientDocumentsTable: React.FC<{openDrawer: () => void; clientId?: string}> = ({openDrawer, clientId}) => {
  const visitDocuments = useSelector(
    (state: RootState) => state.visitDocument.visitDocuments,
  );
  const dispatch = useDispatch<AppDispatch>();
  const pagy = useSelector((state: RootState) => state.visitDocument.pagy);
  const activeTab = useSelector((state: RootState) => state.client.activeTab);
  const {setDotsMenuIsOpen} = useDropdownActionMenu();
  const loading = useSelector(
    (state: RootState) => state.visitDocument.status === 'loading',
  );
  const navigate = useNavigate();

  const [visibleColumns, setVisibleColumns] = React.useState<VisibleColumnsType>({
    id: true,
    visitId: true,
    documentTemplate: true,
    status: true,
    createdAt: true,
    actions: true,
  });

  const columnOptions = [
    {value: 'id', label: 'ID', isVisible: visibleColumns.id},
    {value: 'visitId', label: 'Visit ID', isVisible: visibleColumns.visitId},
    {value: 'documentTemplate', label: 'Document Template', isVisible: visibleColumns.documentTemplate},
    {value: 'status', label: 'Status', isVisible: visibleColumns.status},
    {value: 'createdAt', label: 'Created At', isVisible: visibleColumns.createdAt},
    {value: 'actions', label: 'Actions', isVisible: visibleColumns.actions},
  ];

  const searchByOptions = [
    {
      value: 'id',
      label: 'ID',
    },
    {
      value: 'visitId',
      label: 'Visit ID',
    },
  ];

  const fetchData = async ({
    page, search, sortField, sortDirection, rowsPerPage,
  }: {
    page: number;
    search: SearchType<{[key: string]: {operator: string; value: string}}>;
    sortField: string;
    sortDirection: string;
    rowsPerPage: number;
  }): Promise<void> => {
    if (clientId) {
      // Filter by client through the visit relationship
      search.where = {
        ...search.where,
        'client_visit.client_id': {
          operator: '==',
          value: clientId,
        },
      };
    }

    await dispatch(
      VisitDocumentThunks.index({
        page, search, sortField, sortDirection, rowsPerPage,
      }),
    );
  };

  useEffect(() => {
    fetchData({
      page: pagy.page,
      search: {where: {}},
      sortField: 'createdAt',
      sortDirection: 'desc',
      rowsPerPage: pagy.items,
    });
  }, [activeTab, dispatch, pagy.page, pagy.items]);

  const handleColumnToggle = (column: string) => {
    if (column in visibleColumns) {
      setVisibleColumns(prev => ({
        ...prev,
        [column]: !prev[column as keyof VisibleColumnsType],
      }));
    }
  };

  const handleOpenDrawer = (id: string) => async () => {
    dispatch(setIsEditing(false));
    openDrawer();
    await dispatch(VisitDocumentThunks.show(id));
  };

  const handleFillDocument = (visitDocument: VisitDocument) => {
    const documentTemplateSolution = visitDocument.documentTemplateSolution;
    if (documentTemplateSolution?.id) {
      navigate(`/document-fill/${documentTemplateSolution.id}`);
    } else {
      console.error('No document template solution found for this visit document');
    }
  };

  const handleVisitClick = (visitId: string) => {
    navigate(`/visits/${visitId}`);
  };

  return (
    <Table
      fetchData={fetchData}
      pagy={pagy}
      activeTab={activeTab}
      setActiveTab={(tab: string) => dispatch(setActiveTab(tab))}
      tabs={['Visits', 'Documents', 'Notes']}
      pageable={true}
      recordsName={'Documents'}
      searchable={true}
      columns={true}
      loading={loading}
      columnOptions={columnOptions}
      onColumnToggle={handleColumnToggle}
      searchByOptions={searchByOptions}
      blueButton={null}>
      <thead>
        <tr>
          {visibleColumns.id && <TableHeader label="ID" sortBy="id" />}
          {visibleColumns.visitId && <TableHeader label="Visit ID" sortBy="visitId" />}
          {visibleColumns.documentTemplate && <TableHeader label="Document Template" sortBy="documentTemplate" />}
          {visibleColumns.status && <TableHeader label="Status" sortBy="status" />}
          {visibleColumns.createdAt && <TableHeader label="Created At" sortBy="createdAt" />}
          {visibleColumns.actions && <TableHeader label="Actions" />}
        </tr>
      </thead>
      <tbody className="divide-y divide-gray-200 dark:divide-neutral-700">
        {visitDocuments.map((visitDocument: VisitDocument) => (
          <TableRow key={visitDocument.id}>
            {visibleColumns.id && (
              <TableCell>
                <div className="relative group">
                  <div className="w-full flex items-center gap-x-3">
                    <div className="flex-grow">
                      <span className="text-sm font-medium text-gray-700 dark:text-neutral-300">
                        {visitDocument.id}
                      </span>
                    </div>
                  </div>
                </div>
              </TableCell>
            )}
            {visibleColumns.visitId && (
              <TableCell>
                <span
                  className="text-sm cursor-pointer font-medium dark:text-neutral-200 underline text-cyan-600 hover:text-cyan-800"
                  onClick={() => handleVisitClick(visitDocument.clientVisitId)}>
                  {visitDocument.clientVisitId}
                </span>
              </TableCell>
            )}
            {visibleColumns.documentTemplate && (
              <TableCell>
                {visitDocument.documentTemplate?.name || '-'}
              </TableCell>
            )}
            {visibleColumns.status && (
              <TableCell>
                <StatusBadge status={visitDocument.status ?? 'Unknown'} />
              </TableCell>
            )}
            {visibleColumns.createdAt && (
              <TableCell>
                {visitDocument.createdAt || '-'}
              </TableCell>
            )}
            {visibleColumns.actions && (
              <TableCell>
                <DotsMenu options={[
                  {
                    label: 'Fill Document',
                    onClick: () => handleFillDocument(visitDocument),
                    hoverClass: 'hover:bg-cyan-100',
                  },
                  // {
                  //   label: 'Edit',
                  //   onClick: () => handleOpenDrawer(visitDocument.id)(),
                  //   hoverClass: 'hover:bg-cyan-100',
                  // },
                ]} />
              </TableCell>
            )}
          </TableRow>
        ))}
      </tbody>
    </Table>
  );
};

export default ClientDocumentsTable;
